/**
 * Iter105: Keyboard Shortcuts Cheatsheet Modal.
 *
 * Wird via Shift+? geöffnet — zeigt alle verfügbaren Shortcuts gruppiert.
 */
import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Keyboard } from "lucide-react";
import { SHORTCUT_MAP } from "@/hooks/useKeyboardShortcuts";

function Kbd({ children }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[24px] h-[24px] px-1.5 rounded border border-slate-300 bg-slate-50 text-[11px] font-mono font-medium text-slate-700 shadow-sm">
      {children}
    </kbd>
  );
}

function Sequence({ keys }) {
  return (
    <span className="inline-flex items-center gap-1">
      {keys.map((k, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="text-slate-400 text-[10px]">→</span>}
          <Kbd>{k.toUpperCase()}</Kbd>
        </React.Fragment>
      ))}
    </span>
  );
}

export function ShortcutsCheatsheet({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl"
        data-testid="shortcuts-cheatsheet-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Keyboard className="h-5 w-5 text-violet-600" />
            Tastatur-Shortcuts
          </DialogTitle>
          <DialogDescription>
            Wie Gmail/Linear — Tasten nacheinander drücken (innerhalb 1.5 Sekunden).
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Navigation</div>
            <ul className="space-y-1.5 text-sm">
              {SHORTCUT_MAP.map((s) => (
                <li key={s.sequence.join("")} className="flex items-center justify-between gap-3">
                  <span className="text-slate-700">{s.label}</span>
                  <Sequence keys={s.sequence} />
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Aktionen</div>
            <ul className="space-y-1.5 text-sm">
              <li className="flex items-center justify-between gap-3">
                <span className="text-slate-700">Globale Suche</span>
                <span className="inline-flex items-center gap-1">
                  <Kbd>⌘</Kbd>
                  <span className="text-slate-400 text-[10px]">+</span>
                  <Kbd>K</Kbd>
                </span>
              </li>
              <li className="flex items-center justify-between gap-3">
                <span className="text-slate-700">Cheatsheet öffnen</span>
                <span className="inline-flex items-center gap-1">
                  <Kbd>Shift</Kbd>
                  <span className="text-slate-400 text-[10px]">+</span>
                  <Kbd>?</Kbd>
                </span>
              </li>
              <li className="flex items-center justify-between gap-3">
                <span className="text-slate-700">Schliessen</span>
                <Kbd>Esc</Kbd>
              </li>
            </ul>

            <div className="text-xs font-semibold text-slate-500 uppercase mb-2 mt-5">Tipps</div>
            <ul className="space-y-1.5 text-[12px] text-slate-600">
              <li>• Shortcuts wirken nicht in Eingabefeldern (Input/Textarea).</li>
              <li>• ⌘K Suche akzeptiert auch natürliche Sprache (z.B. "offene Rechnungen").</li>
              <li>• In der Suche wechselst du mit ↑/↓ + ↵ zwischen Treffern.</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
